import { useMemo } from 'react';
import type { EduRanking } from 'interfaces/EduRanking';
import Tabs from 'components/Globals/Tabs';
import RankingCardList, { type Variant } from 'components/education/EduRanking/RankingCardList';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import * as S from './ChildRankingsSectionStyles';

export interface ChildRankingsSectionProps {
  childRankings: EduRanking['childRankings'];
  types?: { name: string; type: string }[];
  variant: Variant;
  sectionTitle?: string;
}

const ChildRankingsSection = ({ childRankings, types, variant, sectionTitle }: ChildRankingsSectionProps) => {
  const childRankingsByType = useMemo(() => {
    if (!childRankings || childRankings.length === 0 || !types || types.length === 0) return null;
    return types
      .map(({ name, type }) => {
        const childRankingsList = childRankings.filter((childRanking) => childRanking.childType === type);
        return { childRankings: childRankingsList, name, type };
      })
      .filter(({ childRankings: chRankings }) => chRankings.length > 0);
  }, [childRankings, types]);

  if (!childRankings || childRankings.length === 0) return null;

  return (
    <S.SectionWrapper>
      {sectionTitle && (
        <S.SectionTitle
          $fontFamily='saol'
          $size={{ default: 'level3', xlMin: 'level2' }}
        >
          {sectionTitle}
        </S.SectionTitle>
      )}
      {childRankingsByType && (
        <Tabs
          config={childRankingsByType.map(({ name, type, childRankings: rankings }) => ({
            children: (
              <RankingCardList
                key={type}
                rankings={rankings.slice(0, 4)}
                variant={variant}
              />
            ),
            title: name,
          }))}
          defaultIndex={0}
          variant='buttons'
        />
      )}
      {!childRankingsByType && (
        <RankingCardList
          rankings={childRankings}
          variant={variant}
        />
      )}
    </S.SectionWrapper>
  );
};

export default withErrorBoundary(ChildRankingsSection, { fragment: UIFragments.EDU_CHILD_RANKINGS });
